import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { getLocalUser } from '../utils/authHelpers';

type AgentType = 'call' | 'email' | 'linkedin' | 'whatsapp' | 'leadgen';

interface AgentInsightsData {
  dataCard1: string;
  dataCard2: string;
  dataCard3: string;
  aiInsight1: string;
  aiInsight2: string;
  aiInsight3: string;
  computedAt: string | null;
}

interface UseAgentInsightsReturn extends AgentInsightsData {
  isLoading: boolean;
  isRefreshing: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useAgentInsights(agentType: AgentType): UseAgentInsightsReturn {
  const [data, setData] = useState<AgentInsightsData>({
    dataCard1: '',
    dataCard2: '',
    dataCard3: '',
    aiInsight1: '',
    aiInsight2: '',
    aiInsight3: '',
    computedAt: null,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchInsights = useCallback(async () => {
    const localUser = getLocalUser();
    const clientId = localUser?.client_id;
    if (!clientId) {
      setIsLoading(false);
      return;
    }

    try {
      const { data: row, error: fetchError } = await supabase
        .from('agent_insights')
        .select('data_card_1, data_card_2, data_card_3, ai_insight_1, ai_insight_2, ai_insight_3, computed_at')
        .eq('client_id', clientId)
        .eq('agent_type', agentType)
        .maybeSingle();

      if (fetchError) {
        setError(fetchError.message);
      } else if (row) {
        setData({
          dataCard1: row.data_card_1 || '',
          dataCard2: row.data_card_2 || '',
          dataCard3: row.data_card_3 || '',
          aiInsight1: row.ai_insight_1 || '',
          aiInsight2: row.ai_insight_2 || '',
          aiInsight3: row.ai_insight_3 || '',
          computedAt: row.computed_at,
        });
        setError(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load insights');
    } finally {
      setIsLoading(false);
    }
  }, [agentType]);

  useEffect(() => {
    fetchInsights();
  }, [fetchInsights]);

  const refresh = useCallback(async () => {
    const localUser = getLocalUser();
    const clientId = localUser?.client_id;
    if (!clientId) return;

    setIsRefreshing(true);
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const response = await fetch(`${supabaseUrl}/functions/v1/compute-agent-insights`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ client_id: clientId, force_refresh: true }),
      });

      if (!response.ok) {
        const body = await response.text();
        throw new Error(body || `Refresh failed (${response.status})`);
      }

      await fetchInsights();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Refresh failed');
    } finally {
      setIsRefreshing(false);
    }
  }, [fetchInsights]);

  return {
    ...data,
    isLoading,
    isRefreshing,
    error,
    refresh,
  };
}
